import { ISubQuoteProduct } from '../../../types';
import {
  FireEgressInput,
  FireEgressWindowType,
  FireEgressBadgeStatus,
  SashOperationValue,
  calculateFireEgress,
  calculateFireEgressMultiFrame,
  resolveSashHeights,
} from '@/features/assistant/utils/fireEgress';

export const SIDEBAR_SUBTITLE_TYPES = ['Profile Colour Type', 'Security', 'Horns', 'Window Type'];

export const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

export const getSubProductOptionType = (p: ISubQuoteProduct): number | null => {
  const raw = p.dogma_optiontype;
  if (raw !== undefined && raw !== null) {
    const n = Number(raw);
    if (!isNaN(n)) return n;
  }
  for (const field of [p.dogma_valuejson, p.dogma_value]) {
    try {
      const parsed = JSON.parse(field || '');
      if (parsed?.optionType !== undefined) return Number(parsed.optionType);
    } catch {}
  }
  if (p.dogma_optionname === 'Variation') return 64;
  return null;
};

/** Expand form-type / dimension sub-products into individual label+value rows. */
export const getObjectRows = (products: ISubQuoteProduct[]): { key: string; val: string }[] | null => {
  if (products.length > 1) {
    const rows = products
      .map(p => ({ key: p.dogma_name || '', val: p.dogma_value || '' }))
      .filter(r => r.key);
    if (rows.length > 0) return rows;
  }

  for (const p of products) {
    if (!p.dogma_value) continue;
    try {
      const parsed = JSON.parse(p.dogma_value);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed) &&
        !('label' in parsed) &&
        !('name' in parsed) &&
        !('value' in parsed) &&
        !('optionType' in parsed) &&
        !('optionId' in parsed)
      ) {
        const entries = Object.entries(parsed);
        if (entries.length >= 1) {
          return entries.map(([k, v]) => ({ key: k, val: String(v) }));
        }
      }
    } catch {}
  }
  return null;
};

export const parseDisplayValue = (value: string | undefined, fallback: string): string => {
  if (!value) return fallback;
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      const labels = parsed
        .map((item) =>
          typeof item === 'object' && item !== null
            ? (item.label || item.name || item.value || String(item))
            : String(item),
        )
        .filter(Boolean);
      return labels.length > 0 ? labels.join(', ') : fallback;
    }
    if (typeof parsed === 'object' && parsed !== null) {
      if (parsed.label || parsed.name || parsed.value) {
        return parsed.label || parsed.name || parsed.value;
      }
      return Object.entries(parsed)
        .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
        .join(', ');
    }
    return String(parsed);
  } catch {
    return value;
  }
};

/** Derive window type from sub-products.
 *  Checks dogma_optionname === 'Window Type' first (same as getSidebarSubtitle),
 *  then falls back to optionType === 0 for older records. */
export const extractWindowTypeFromSubProducts = (subs: ISubQuoteProduct[]): FireEgressWindowType => {
  // Primary: find by dogma_optionname (matches how getSidebarSubtitle works)
  const windowTypeSub =
    subs.find((p) => p.dogma_optionname === 'Window Type') ??
    subs.find((p) => getSubProductOptionType(p) === 0);
  if (!windowTypeSub) return 'unknown';

  // The window type text may be in dogma_name or dogma_value
  const raw = (windowTypeSub.dogma_name ?? '') + ' ' + (() => {
    if (!windowTypeSub.dogma_value) return '';
    try {
      const parsed = JSON.parse(windowTypeSub.dogma_value);
      return typeof parsed === 'string' ? parsed : (parsed?.label ?? parsed?.name ?? parsed?.optionText ?? '');
    } catch {
      return windowTypeSub.dogma_value;
    }
  })();
  const name = raw.toLowerCase();
  if (name.includes('arch')) return 'arch';
  if (name.includes('bay')) return 'bay';
  if (name.includes('couple') || name.includes('coupled')) return 'couple';
  return 'standard';
};

/** Parse a numeric measurement value from dogma_value (handles plain string or JSON). */
const parseMeasurementNumber = (raw?: string): number => {
  if (!raw) return 0;
  // Plain numeric string e.g. "1000"
  const direct = Number(raw);
  if (isFinite(direct) && direct > 0) return direct;
  // JSON-encoded e.g. '"1000"' or '{"value":1000}'
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'number' && isFinite(parsed) && parsed > 0) return parsed;
    if (typeof parsed === 'string') {
      const n = Number(parsed);
      if (isFinite(n) && n > 0) return n;
    }
  } catch {}
  return 0;
};

/**
 * Extract fire egress input (width, height, window type) from sub-products.
 * Returns null when critical dimensions are missing.
 */
export const extractFireEgressFromSubProducts = (
  subs: ISubQuoteProduct[],
): (FireEgressInput & { frameWidths?: number[]; frameSashOperations?: (SashOperationValue | undefined)[] }) | null => {
  const windowType = extractWindowTypeFromSubProducts(subs);
  if (windowType === 'unknown') return null;

  const measureSubs = subs.filter((p) => p.dogma_categoryname === 'Measurements');

  let windowWidth: number | undefined;
  let windowHeight: number | undefined;
  const frameWidths: number[] = [];
  let topSash: number | undefined;
  let bottomSash: number | undefined;

  for (const p of measureSubs) {
    const name = (p.dogma_name ?? '').toLowerCase().replace(/[\s()]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    const val = parseMeasurementNumber(p.dogma_value);
    if (val <= 0) continue;

    if (name.includes('overall_height') || name === 'height_mm') {
      windowHeight = val;
    } else if (name.includes('overall_width') || name === 'width_mm') {
      windowWidth = val;
    } else if (/window_\d+_width/.test(name)) {
      frameWidths.push(val);
    } else if (name.includes('top_sash')) {
      topSash = val;
    } else if (name.includes('bottom_sash')) {
      bottomSash = val;
    }
  }

  if (!windowHeight) return null;
  const effectiveWidth = frameWidths.length > 0 ? frameWidths[0] : windowWidth;
  if (!effectiveWidth && frameWidths.length === 0) return null;

  const sash = extractSashOperationsFromSubProducts(subs);

  return {
    windowType,
    windowWidth: effectiveWidth ?? 0,
    windowHeight,
    topSash,
    bottomSash,
    frameWidths: frameWidths.length > 1 ? frameWidths : undefined,
    sashOperation: sash.sashOperation,
    frameSashOperations: sash.frameSashOperations,
  };
};

export type EgressBadgeStatus = FireEgressBadgeStatus;

/** Extract per-frame (Individual Configuration) and single ("same for all") sash
 *  operations from sub-products, mirroring extractDimensionsFromMessages. */
export const extractSashOperationsFromSubProducts = (
  subs: ISubQuoteProduct[],
): { sashOperation?: SashOperationValue; frameSashOperations?: (SashOperationValue | undefined)[] } => {
  let sashOperation: SashOperationValue | undefined;
  const frameSashOperations: (SashOperationValue | undefined)[] = [];

  for (const p of subs) {
    const ot = getSubProductOptionType(p);
    const isSash = ot === 7 || /sash/i.test(p.dogma_optionname ?? '');
    if (!isSash) continue;

    let value: string | undefined;
    if ((p as any).dogma_valuejson) {
      try {
        const opt = JSON.parse((p as any).dogma_valuejson);
        if (opt?.responseValue) value = String(opt.responseValue);
      } catch {}
    }
    if (!value) {
      const raw = (p.dogma_value ?? p.dogma_name ?? '').toLowerCase();
      if (raw.includes('both_sashes_fixed')) value = 'both_sashes_fixed';
      else if (raw.includes('bottom_sash_fixed')) value = 'bottom_sash_fixed';
      else if (raw.includes('both_sashes_slide')) value = 'both_sashes_slide';
      else if (raw.includes('top_sash_fixed')) value = 'top_sash_fixed';
    }
    if (!value) continue;

    let frameIdx: number | undefined;
    const qj = (p as any).dogma_questionjson;
    if (qj) {
      try {
        const msg = JSON.parse(qj);
        const rp = msg?.question?.responsePath ?? '';
        const m = rp.match(/frame(\d+)$/i);
        if (m) frameIdx = Number(m[1]) - 1;
      } catch {}
    }

    if (frameIdx !== undefined) {
      frameSashOperations[frameIdx] = value as SashOperationValue;
    } else {
      sashOperation = value as SashOperationValue;
    }
  }

  return {
    sashOperation,
    frameSashOperations: frameSashOperations.length > 0 ? frameSashOperations : undefined,
  };
};

/** Resolve the Sash operation response value from sub-products.
 *  Single-select answers store the full IQuestionOption JSON in dogma_valuejson
 *  (with responseValue + optionType); getSubProductOptionType returns null for
 *  these, so we also fall back to /sash/i on dogma_optionname and value sniffing. */
export const getSashResponseValueFromSubProducts = (subs: ISubQuoteProduct[]): string | undefined => {
  for (const p of subs) {
    const ot = getSubProductOptionType(p);
    const isSash = ot === 7 || /sash/i.test(p.dogma_optionname ?? '');
    if (!isSash) continue;
    if ((p as any).dogma_valuejson) {
      try {
        const opt = JSON.parse((p as any).dogma_valuejson);
        if (opt?.responseValue) return String(opt.responseValue);
      } catch {}
    }
    const raw = (p.dogma_value ?? p.dogma_name ?? '').toLowerCase();
    if (raw.includes('both_sashes_fixed')) return 'both_sashes_fixed';
    if (raw.includes('bottom_sash_fixed')) return 'bottom_sash_fixed';
    if (raw.includes('both_sashes_slide')) return 'both_sashes_slide';
    if (raw.includes('top_sash_fixed')) return 'top_sash_fixed';
  }
  return undefined;
};

/** True when any sub-product records egress = Yes (optionType 48).
 *  Fallback: dogma_optionname may store the question name (e.g. "Egress Compliant") instead of numeric optionType. */
export const isEgressWindowFromSubProducts = (subs: ISubQuoteProduct[]): boolean =>
  subs.some((p) => {
    const ot = getSubProductOptionType(p);
    const isEgressByType = ot === 48;
    const isEgressByName = /egress/i.test(p.dogma_optionname ?? '');
    if (!isEgressByType && !isEgressByName) return false;
    const displayVal = parseDisplayValue(p.dogma_value, p.dogma_name ?? '').toLowerCase();
    return displayVal.includes('yes');
  });

/** Resolve fire egress badge status from sub-products.
 *  Returns null when egress was not answered Yes or window type is not applicable. */
export const getEgressBadgeFromSubProducts = (subs: ISubQuoteProduct[]): EgressBadgeStatus => {
  if (!isEgressWindowFromSubProducts(subs)) return null;

  const input = extractFireEgressFromSubProducts(subs);
  if (!input) return { compliant: false };
  if (input.windowType === 'arch') return null;

  const sashes = resolveSashHeights('custom', input.windowHeight, input.topSash, input.bottomSash);

  if (input.frameWidths && input.frameWidths.length > 1) {
    const multi = calculateFireEgressMultiFrame(
      input.frameWidths,
      input.windowType,
      input.windowHeight,
      sashes.topSash,
      sashes.bottomSash,
      input.frameSashOperations,
    );
    if (!multi.anyCompliant) return { compliant: false };
    const passing = multi.frames.find((f) => f.result.applicable && f.result.pass !== 'none');
    if (!passing || !passing.result.applicable) return { compliant: false };
    const pass: 'sliding' | 'tilting' | 'both' = passing.result.pass === 'both' ? 'both' : passing.result.pass === 'tilting' ? 'tilting' : 'sliding';
    return { compliant: true, pass };
  }

  const w = input.windowWidth > 0 ? input.windowWidth : input.frameWidths?.[0];
  if (!w) return { compliant: false };

  const result = calculateFireEgress({
    windowType: input.windowType,
    windowWidth: w,
    windowHeight: input.windowHeight,
    topSash: sashes.topSash,
    bottomSash: sashes.bottomSash,
    sashOperation: input.sashOperation,
  });
  if (!result.applicable) return null;
  if (result.pass === 'none') return { compliant: false };
  const pass: 'sliding' | 'tilting' | 'both' = result.pass === 'both' ? 'both' : result.pass === 'tilting' ? 'tilting' : 'sliding';
  return { compliant: true, pass };
};

export const getSidebarSubtitle = (subs: ISubQuoteProduct[]): string =>
  SIDEBAR_SUBTITLE_TYPES
    .map(typeName => {
      const sp = subs.find(p => p.dogma_optionname === typeName);
      if (!sp) return '';
      return parseDisplayValue(sp.dogma_value, sp.dogma_name || '');
    })
    .filter(Boolean)
    .join(' – ');
